import React, { useState, useEffect } from 'react';

/**
 * NumericInput - Campo de entrada numérico mejorado
 * Se comporta como un campo de texto pero solo acepta números
 * No fuerza "0" cuando el usuario borra el contenido
 * Envía valores numéricos al backend
 * 
 * @param {number|string} value - Valor actual (puede ser número o string)
 * @param {function} onChange - Callback cuando cambia el valor (recibe número)
 * @param {string} placeholder - Placeholder del input
 * @param {number} min - Valor mínimo permitido
 * @param {number} step - Incremento/decremento
 * @param {string} className - Clases CSS adicionales
 * @param {function} onKeyDown - Callback para eventos de teclado
 * @param {boolean} disabled - Si el input está deshabilitado
 * @param {string} title - Tooltip del input
 * @param {number} decimals - Cantidad de decimales permitidos (default: 2)
 */
export default function NumericInput({
  value,
  onChange,
  placeholder = '0.00',
  min = 0,
  step = 0.01,
  className = '',
  onKeyDown,
  disabled = false,
  title = '',
  decimals = 2
}) {
  // Estado interno para manejar el valor como string mientras el usuario escribe
  const [internalValue, setInternalValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Sincronizar valor externo con interno
  useEffect(() => {
    if (!isFocused) {
      // Solo actualizar cuando no está enfocado para no interrumpir la escritura del usuario
      if (value === 0 || value === '0' || value === null || value === undefined || value === '') {
        setInternalValue('');
      } else {
        setInternalValue(String(value));
      }
    }
  }, [value, isFocused]);

  const handleChange = (e) => {
    let inputValue = e.target.value;
    
    // Permitir string vacío (usuario borrando)
    if (inputValue === '') {
      setInternalValue('');
      onChange(0); // Enviar 0 al backend pero no mostrarlo en la UI
      return;
    }

    // Permitir solo números, punto decimal y un signo negativo al inicio
    // Regex: permite números, un punto decimal, y opcionalmente un signo negativo al inicio
    const regex = /^-?\d*\.?\d*$/;
    
    if (!regex.test(inputValue)) {
      // Si no cumple el regex, no actualizar
      return;
    }

    // Verificar que no haya más de un punto decimal
    const decimalCount = (inputValue.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return;
    }

    // Si hay punto decimal, verificar que no exceda la cantidad de decimales permitidos
    if (inputValue.includes('.')) {
      const parts = inputValue.split('.');
      if (parts[1] && parts[1].length > decimals) {
        return;
      }
    }

    // Actualizar valor interno
    setInternalValue(inputValue);

    // Convertir a número para enviar al onChange
    const numValue = parseFloat(inputValue);
    
    // Si es un número válido, enviarlo; si no, enviar 0
    if (!isNaN(numValue)) {
      onChange(numValue);
    } else {
      // Si el usuario está escribiendo (ej: solo un punto "." o un guión "-"), mantener 0
      onChange(0);
    }
  };

  const handleFocus = (e) => {
    setIsFocused(true);
    
    // Si el valor es 0 o vacío, limpiar el campo para facilitar la entrada
    if (value === 0 || value === '0' || value === '' || internalValue === '0') {
      setInternalValue('');
    }
    
    // Seleccionar todo el texto para facilitar el reemplazo
    e.target.select();
  };

  const handleBlur = () => {
    setIsFocused(false);
    
    // Al perder el foco, formatear el número
    if (internalValue === '' || internalValue === '-' || internalValue === '.') {
      setInternalValue('');
      onChange(0);
    } else {
      const numValue = parseFloat(internalValue);
      if (!isNaN(numValue)) {
        // Aplicar mínimo si está configurado
        const finalValue = Math.max(min, numValue);
        
        // Formatear con decimales especificados
        const formatted = finalValue.toFixed(decimals);
        setInternalValue(formatted);
        onChange(finalValue);
      } else {
        setInternalValue('');
        onChange(0);
      }
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={internalValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      title={title}
      autoComplete="off"
    />
  );
}

